const DAY_MS = 86400000;

export const DEFAULT_INTERVAL = "DAY";
export const DEFAULT_METRIC = "REVENUE";
export const DEFAULT_PRODUCT_JOIN = "sku";
export const DEFAULT_PRODUCT_SORT = "REVENUE";
export const PRODUCT_JOIN_OPTIONS = [
  { value: "sku", label: "SKU (recommended)" },
  { value: "name", label: "Product name" },
  { value: "barcode", label: "Barcode" },
];

export const INSIGHTS_INTERVAL_OPTIONS = [
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
];

export const INSIGHTS_METRIC_OPTIONS = [
  { value: "REVENUE", label: "Revenue" },
  { value: "SALES_COUNT", label: "Sales count" },
];

export const PRODUCT_SORT_OPTIONS = [
  { value: "REVENUE", label: "Revenue" },
  { value: "QUANTITY", label: "Quantity sold" },
  { value: "STORE_COVERAGE", label: "Store coverage" },
  { value: "NAME", label: "Product name" },
];

export function productJoinWarning(joinBy) {
  if (joinBy === "name") {
    return "Product names can change or collide, so matches may be less reliable. Ambiguous products remain separate.";
  }
  if (joinBy === "barcode") {
    return "Barcodes can be missing, duplicated, or changed, so matches may be less reliable. Ambiguous products remain separate.";
  }
  return "";
}

export function formatProductQuantity(partitions = []) {
  if (!partitions.length) return "Unavailable (unit not reported)";
  return partitions
    .map(
      (partition) =>
        `${Number(partition.quantitySold || 0).toLocaleString()} ${partition.unit}`,
    )
    .join(" · ");
}

export function localDate(date) {
  const value = date instanceof Date ? date : new Date(date);
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
}

export function defaultDateRange(today = new Date()) {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { startDate: localDate(start), endDate: localDate(end) };
}

export function inclusiveDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.floor((end - start) / DAY_MS) + 1;
}

export function parseFilters(query = {}, defaults = defaultDateRange()) {
  const stores = String(query.stores || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  return {
    storeIds: [...new Set(stores)],
    startDate: /^\d{4}-\d{2}-\d{2}$/.test(query.start || "")
      ? query.start
      : defaults.startDate,
    endDate: /^\d{4}-\d{2}-\d{2}$/.test(query.end || "")
      ? query.end
      : defaults.endDate,
    interval: String(query.interval || DEFAULT_INTERVAL),
    metric: String(query.metric || DEFAULT_METRIC),
    joinBy: ["sku", "name", "barcode"].includes(String(query.join_by))
      ? String(query.join_by)
      : DEFAULT_PRODUCT_JOIN,
    productSortBy: ["REVENUE", "QUANTITY", "STORE_COVERAGE", "NAME"].includes(
      String(query.product_sort),
    )
      ? String(query.product_sort)
      : DEFAULT_PRODUCT_SORT,
    productSortDirection: String(query.product_direction) === "ASC"
      ? "ASC"
      : "DESC",
    productSortCurrency: String(query.product_currency || "ALL"),
    productSortUnit: String(query.product_unit || ""),
  };
}

export function filtersToQuery(filters) {
  return {
    stores: filters.storeIds.join(","),
    start: filters.startDate,
    end: filters.endDate,
    interval: filters.interval,
    metric: filters.metric,
    join_by: filters.joinBy,
    product_sort: filters.productSortBy || DEFAULT_PRODUCT_SORT,
    product_direction: filters.productSortDirection || "DESC",
    ...(filters.productSortCurrency
      ? { product_currency: filters.productSortCurrency }
      : {}),
    ...(filters.productSortUnit
      ? { product_unit: filters.productSortUnit }
      : {}),
  };
}

export function validateFilters(filters, limits = {}) {
  if (!filters.storeIds.length) return "Select at least one store.";
  const maxStores = Number(limits.maxStores || 0);
  if (maxStores && filters.storeIds.length > maxStores) {
    return `Select no more than ${maxStores} stores.`;
  }
  const days = inclusiveDays(filters.startDate, filters.endDate);
  if (days < 1) return "End date must be on or after start date.";
  const maxDays = Number(limits.maxRangeDays || 0);
  if (maxDays && days > maxDays) {
    return `Date range cannot exceed ${maxDays} days.`;
  }
  return "";
}

export function normalizedFeatureValue(source, key = "INSIGHTS") {
  const values = [
    source?.feature_flags,
    source?.features,
    source?.subscription?.features,
    source?.release_features,
  ];
  for (const value of values) {
    if (Array.isArray(value)) {
      if (value.includes(key)) return true;
      continue;
    }
    if (value && Object.prototype.hasOwnProperty.call(value, key)) {
      const setting = value[key];
      return typeof setting === "object" ? setting.enabled !== false : Boolean(setting);
    }
  }
  return undefined;
}

export function canShowInsights(user, stores = [], cachedEligibility = null) {
  if (!user) return false;
  if (normalizedFeatureValue(user) === false) return false;
  if (cachedEligibility !== null) return cachedEligibility;
  if (!stores.length) return true;
  return stores.some((store) => {
    if (store.isOwner || store.is_owner) return true;
    const permissions = (store.permissions || []).map((value) =>
      String(value).toUpperCase(),
    );
    return permissions.some(
      (permission) =>
        permission.includes("REPORT") || permission.includes("ANALYTIC"),
    );
  });
}

export function drillDownLocation(storeId, filters) {
  return {
    name: "store_dashboard",
    params: { storeId },
    query: {
      start_date: filters.startDate,
      end_date: filters.endDate,
    },
  };
}

export function applyGroupPreset(group, authorizedIds, maxStores = 0) {
  const allowed = new Set(authorizedIds.map(String));
  const sourceIds = group.store_ids || group.storeIds || group.stores || [];
  const selected = sourceIds
    .map((store) => String(store?.id ?? store))
    .filter((id) => allowed.has(id));
  return maxStores ? selected.slice(0, maxStores) : selected;
}

export function partitionByCurrency(rows = []) {
  return rows.reduce((groups, row) => {
    const currency = row.currency || row.currency_code || "Unspecified";
    if (!groups[currency]) groups[currency] = [];
    groups[currency].push(row);
    return groups;
  }, {});
}

export function addCurrencyShares(rows = []) {
  const totals = rows.reduce((values, row) => {
    const currency = row.currency || "Unspecified";
    values[currency] = (values[currency] || 0) + Number(row.revenue || 0);
    return values;
  }, {});
  return rows.map((row) => ({
    ...row,
    share:
      row.share ??
      (totals[row.currency || "Unspecified"]
        ? (Number(row.revenue || 0) / totals[row.currency || "Unspecified"]) * 100
        : null),
  }));
}

function trendPointLabel(point) {
  return point?.bucket ?? point?.date ?? point?.period ?? point?.label ?? null;
}

export function buildCurrencyChartData(trends, stores, currency) {
  const source = trends?.series || [];
  const series = Array.isArray(source)
    ? source
    : Object.entries(source).map(([store_id, value]) => ({
        store_id,
        ...value,
      }));
  const storeRows = stores || [];
  const relevant = series.filter((item) => {
    const store = storeRows.find(
      (row) => String(row.id) === String(item.store_id ?? item.id),
    );
    return (item.currency || item.currency_code || store?.currency) === currency;
  });

  const labels = [];
  const seenLabels = new Set();
  relevant.forEach((item) => {
    const itemLabels =
      item.labels ||
      item.dates ||
      (item.points || item.series || []).map(trendPointLabel);
    itemLabels.filter(Boolean).forEach((label) => {
      if (!seenLabels.has(label)) {
        seenLabels.add(label);
        labels.push(label);
      }
    });
  });

  return {
    labels,
    datasets: relevant.map((item) => {
      const store = storeRows.find(
        (row) => String(row.id) === String(item.store_id ?? item.id),
      );
      const points = item.points || item.series || [];
      const itemLabels = item.labels || item.dates;
      const values = item.values || item.data;
      let data;
      if (Array.isArray(values) && Array.isArray(itemLabels)) {
        const valuesByLabel = new Map(
          itemLabels.map((label, index) => [label, values[index]]),
        );
        data = labels.map((label) => valuesByLabel.get(label) ?? null);
      } else if (points.length) {
        const valuesByLabel = new Map(
          points.map((point) => [
            trendPointLabel(point),
            point.value ?? point.amount ?? point.total ?? 0,
          ]),
        );
        data = labels.map((label) => valuesByLabel.get(label) ?? null);
      } else {
        data = Array.isArray(values) ? values : [];
      }
      return {
        label: item.store_name || item.name || store?.name,
        data,
      };
    }),
  };
}

export function hasInsightsData(summary, trends) {
  const rows = summary?.perStore || [];
  const series = trends?.series || [];
  return (
    rows.some(
      (row) =>
        Number(row.metrics?.sales_count || 0) !== 0 ||
        Number(row.metrics?.revenue || 0) !== 0,
    ) ||
    series.some((row) =>
      (row.series || []).some((point) => Number(point.value || 0) !== 0),
    )
  );
}
